import React, { Dispatch, ReactNode, SetStateAction, useEffect, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

import { ChevronLeft, ChevronRight, Close, Refresh } from '@mui/icons-material';
import {
	Alert,
	Box,
	Checkbox,
	CircularProgress,
	IconButton,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Typography,
} from '@mui/material';
import { type SxProps, type Theme, alpha } from '@mui/material/styles';

import { BoxResizeHandle } from '../../common/components/BoxResizeHandle';

export interface SidePanelListColumn {
	id: string;
	label: string;
	align?: 'left' | 'center' | 'right';
	padding?: 'checkbox' | 'none' | 'normal';
	width?: string | number;
	flex?: number;
	sortable?: boolean;
	sortKey?: string;
}

interface SidePanelListProps<T> {
	title: string;
	subtitle?: string;
	items: T[];
	columns: SidePanelListColumn[];
	isLoading: boolean;
	isFetching?: boolean;
	error?: Error | null;
	emptyMessage?: string;
	selectedItemId?: string | null;
	zIndex?: number;
	top?: number;
	bottom?: number;
	rightOffset?: number;
	width?: number;
	collapsedWidth?: number;
	isCollapsed?: boolean;
	positioning?: 'fixed' | 'absolute' | 'none'; // 'fixed' for viewport (default), 'absolute' for parent container, 'none' for no positioning override
	onClose: () => void;
	onRefresh: () => void;
	onItemSelect: (item: T) => void;
	renderCell: (item: T, columnId: string) => ReactNode;
	getItemId: (item: T) => string;
	getRowSx?: (item: T) => SxProps<Theme> | undefined;
	headerActions?: ReactNode;
	refreshBeforeHeaderActions?: boolean;
	belowHeader?: ReactNode;
	footer?: ReactNode;
	onToggleCollapse?: () => void;
	multiSelectEnabled?: boolean;
	selectedItemIds?: string[];
	onToggleItemSelection?: (item: T) => void;
	onToggleSelectAll?: (selected: boolean) => void;
	multiSelectColumnWidth?: number;
	sortBy?: string;
	sortAscending?: boolean;
	onSortChange?: (sortBy: string, sortAscending: boolean) => void;
	resizeStorageKey?: string;
	defaultWidth?: number;
	minWidth?: number;
	maxWidth?: number;
	setWidth?: Dispatch<SetStateAction<number>>;
	tableSx?: SxProps<Theme>;
	virtualized?: boolean;
	onEndReached?: () => void;
	hasMore?: boolean;
	isLoadingMore?: boolean;
}

export default function SidePanelList<T>({
	title,
	subtitle,
	items,
	columns,
	isLoading,
	isFetching = false,
	error,
	emptyMessage = 'No items found',
	selectedItemId,
	zIndex = 1200,
	top = 0,
	bottom = 0,
	rightOffset = 0,
	width = 400,
	collapsedWidth = 48,
	isCollapsed = false,
	positioning = 'fixed',
	onClose,
	onRefresh,
	onItemSelect,
	renderCell,
	getItemId,
	getRowSx,
	headerActions,
	refreshBeforeHeaderActions = false,
	belowHeader,
	footer,
	onToggleCollapse,
	multiSelectEnabled = false,
	selectedItemIds = [],
	onToggleItemSelection,
	onToggleSelectAll,
	multiSelectColumnWidth = 44,
	sortBy,
	sortAscending = true,
	onSortChange,
	resizeStorageKey,
	defaultWidth,
	minWidth,
	maxWidth,
	setWidth,
	tableSx,
	virtualized = false,
	onEndReached,
	hasMore = false,
	isLoadingMore = false,
}: SidePanelListProps<T>) {
	const panelRef = useRef<HTMLDivElement>(null);
	const tableContainerRef = useRef<HTMLDivElement>(null);
	const virtualizer = useVirtualizer({
		count: virtualized ? items.length : 0,
		getScrollElement: () => tableContainerRef.current,
		estimateSize: () => 42,
		overscan: 8,
		getItemKey: (index) => getItemId(items[index]),
	});
	const virtualRows = virtualized ? virtualizer.getVirtualItems() : [];
	const lastVirtualRow = virtualRows[virtualRows.length - 1];

	useEffect(() => {
		if (virtualized && hasMore && !isFetching && !isLoadingMore && lastVirtualRow && lastVirtualRow.index >= items.length - 5) {
			onEndReached?.();
		}
	}, [hasMore, isFetching, isLoadingMore, items.length, lastVirtualRow, onEndReached, virtualized]);
	const effectiveWidth = isCollapsed ? collapsedWidth : width;
	const canResize = !!resizeStorageKey && !!setWidth && !isCollapsed;
	const allSelected = items.length > 0 && selectedItemIds.length === items.length;
	const partiallySelected = selectedItemIds.length > 0 && selectedItemIds.length < items.length;
	const selectionCellSx = {
		width: multiSelectColumnWidth,
		minWidth: multiSelectColumnWidth,
		maxWidth: multiSelectColumnWidth,
		boxSizing: 'border-box',
		px: 0.5,
		py: 0,
		overflow: 'visible',
	};
	const selectionCheckboxSx = { width: 24, height: 24, p: 0.5 };

	return (
		<Paper
			ref={panelRef}
			className="responsive-side-panel"
			sx={{
				...(positioning !== 'none' && {
					position: positioning,
					top,
					right: rightOffset,
					bottom,
				}),
				width: effectiveWidth,
				zIndex,
				display: 'flex',
				flexDirection: 'column',
				border: '1px solid',
				borderColor: (theme) => alpha(theme.palette.divider, 0.45),
				backgroundColor: 'background.paper',
				borderRadius: rightOffset > 0 ? '8px 0 0 8px' : '8px 0 0 8px',
				boxShadow: '-10px 12px 28px rgba(0, 0, 0, 0.28)',
				transition: canResize ? undefined : 'width 0.3s ease-in-out',
				overflowX: 'hidden',
			}}
		>
			{canResize && (
				<BoxResizeHandle
					position="left"
					width={width}
					setWidth={setWidth}
					panelRef={panelRef}
					storageKey={resizeStorageKey}
					defaultWidth={defaultWidth ?? width}
					minWidth={minWidth ?? 200}
					maxWidth={maxWidth ?? 800}
					direction="right"
				/>
			)}

			{/* Header */}
			<Box
				sx={{
					p: 2,
					backgroundColor: 'action.hover',
					borderBottom: '1px solid',
					borderColor: (theme) => alpha(theme.palette.divider, 0.55),
					minHeight: isCollapsed ? 'auto' : undefined,
				}}
			>
				{isCollapsed ? (
					<Stack direction="column" spacing={1} alignItems="center">
						{onToggleCollapse && (
							<IconButton size="small" onClick={onToggleCollapse} title="Expand panel">
								<ChevronLeft />
							</IconButton>
						)}
						<IconButton size="small" onClick={onRefresh} title="Refresh" disabled={isFetching}>
							<Refresh
								sx={{
									animation: isFetching ? 'spin 1s linear infinite' : 'none',
									'@keyframes spin': {
										'0%': { transform: 'rotate(0deg)' },
										'100%': { transform: 'rotate(360deg)' },
									},
								}}
							/>
						</IconButton>
						<IconButton size="small" onClick={onClose} title="Close panel">
							<Close />
						</IconButton>
					</Stack>
				) : (
					<>
						<Stack direction="row" justifyContent="space-between" alignItems="center">
							<Typography variant="h6" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
								{title}
							</Typography>
							<Stack direction="row" spacing={1}>
								{!refreshBeforeHeaderActions && headerActions}
								<IconButton size="small" onClick={onRefresh} title="Refresh" disabled={isFetching}>
									<Refresh
										sx={{
											animation: isFetching ? 'spin 1s linear infinite' : 'none',
											'@keyframes spin': {
												'0%': { transform: 'rotate(0deg)' },
												'100%': { transform: 'rotate(360deg)' },
											},
										}}
									/>
								</IconButton>
								{refreshBeforeHeaderActions && headerActions}
								{onToggleCollapse && (
									<IconButton size="small" onClick={onToggleCollapse} title="Collapse panel">
										<ChevronRight />
									</IconButton>
								)}
								<IconButton size="small" onClick={onClose} title="Close panel">
									<Close />
								</IconButton>
							</Stack>
						</Stack>
						{subtitle && (
							<Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
								{subtitle}
							</Typography>
						)}
						{belowHeader && <Box sx={{ mt: 1 }}>{belowHeader}</Box>}
					</>
				)}
			</Box>

			{/* Content */}
			{!isCollapsed && (
				<Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', backgroundColor: 'background.default' }}>
					{isLoading && (
						<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
							<CircularProgress />
						</Box>
					)}

					{error && (
						<Box sx={{ p: 2 }}>
							<Alert severity="error">Failed to load: {error instanceof Error ? error.message : 'Unknown error'}</Alert>
						</Box>
					)}

					{!isLoading && !error && (
						<TableContainer
							ref={tableContainerRef}
							sx={{
								flex: 1,
								overflow: 'auto',
								'& .MuiTableHead-root .MuiTableCell-root': {
									// `action.selected` is intentionally translucent in several portal themes.
									// Sticky cells need an opaque surface so scrolling rows never show through.
									backgroundColor: 'background.paper',
									borderColor: 'divider',
									boxShadow: (theme) => `inset 0 -1px 0 ${theme.palette.divider}`,
								},
							}}
						>
							<Table stickyHeader size="small" sx={tableSx}>
								<TableHead>
									<TableRow>
										{multiSelectEnabled && (
											<TableCell
												padding="checkbox"
												sx={{
													...selectionCellSx,
													textAlign: 'left',
												}}
											>
												<Checkbox
													size="small"
													checked={allSelected}
													indeterminate={partiallySelected}
													onChange={(event) => onToggleSelectAll?.(event.target.checked)}
													disabled={!onToggleSelectAll || items.length === 0}
													sx={selectionCheckboxSx}
												/>
											</TableCell>
										)}
										{columns.map((column) => {
											const columnSortKey = column.sortKey ?? column.id;
											const isSorted = column.sortable && sortBy === columnSortKey;

											return (
												<TableCell
													key={column.id}
													align={column.align}
													padding={column.padding}
													sortDirection={isSorted ? (sortAscending ? 'asc' : 'desc') : false}
													sx={{ width: column.width, flex: column.flex }}
												>
													{column.sortable && onSortChange ? (
														<TableSortLabel
															active={isSorted}
															direction={isSorted && !sortAscending ? 'desc' : 'asc'}
															onClick={() => onSortChange(columnSortKey, isSorted ? !sortAscending : true)}
														>
															{column.label}
														</TableSortLabel>
													) : (
														column.label
													)}
												</TableCell>
											);
										})}
									</TableRow>
								</TableHead>
								<TableBody>
									{items.length === 0 ? (
										<TableRow>
											<TableCell colSpan={columns.length + (multiSelectEnabled ? 1 : 0)} align="center">
												<Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
													{emptyMessage}
												</Typography>
											</TableCell>
										</TableRow>
									) : (
										<>
										{virtualized && virtualRows[0]?.start > 0 && (
											<TableRow aria-hidden="true">
												<TableCell colSpan={columns.length + (multiSelectEnabled ? 1 : 0)} sx={{ height: virtualRows[0].start, p: 0, border: 0 }} />
											</TableRow>
										)}
																	{(virtualized ? virtualRows.map((row) => items[row.index]) : items).map((item) => {
											const itemId = getItemId(item);
											const isMultiSelected = selectedItemIds.includes(itemId);
											const isSelected = isMultiSelected || selectedItemId === itemId;
											const rowSx = getRowSx?.(item);

											return (
												<TableRow
													key={itemId}
													hover
													selected={isSelected}
													sx={{
														cursor: 'pointer',
														backgroundColor: isSelected ? 'action.selected' : 'inherit',
														'&:hover': {
															backgroundColor: isSelected ? 'action.selected' : 'action.hover',
														},
														...rowSx,
													}}
													onClick={() => onItemSelect(item)}
												>
													{multiSelectEnabled && (
														<TableCell padding="checkbox" onClick={(event) => event.stopPropagation()} sx={selectionCellSx}>
															<Checkbox
																size="small"
																checked={isMultiSelected}
																onChange={() => onToggleItemSelection?.(item)}
																disabled={!onToggleItemSelection}
																sx={selectionCheckboxSx}
															/>
														</TableCell>
													)}
													{columns.map((column) => (
														<TableCell key={column.id} align={column.align} padding={column.padding}>
															{renderCell(item, column.id)}
														</TableCell>
													))}
												</TableRow>
											);
										})}
										{virtualized && virtualRows.length > 0 && virtualRows[virtualRows.length - 1].end < virtualizer.getTotalSize() && (
											<TableRow aria-hidden="true">
												<TableCell colSpan={columns.length + (multiSelectEnabled ? 1 : 0)} sx={{ height: virtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end, p: 0, border: 0 }} />
											</TableRow>
										)}
										{virtualized && isLoadingMore && (
											<TableRow>
												<TableCell colSpan={columns.length + (multiSelectEnabled ? 1 : 0)} align="center" sx={{ py: 1 }}>
													<CircularProgress size={18} />
												</TableCell>
											</TableRow>
										)}
										</>
									)}
								</TableBody>
							</Table>
						</TableContainer>
					)}
					{footer && !isLoading && !error && <Box sx={{ p: 1, backgroundColor: 'action.hover', borderTop: '1px solid', borderColor: 'divider' }}>{footer}</Box>}
				</Box>
			)}
		</Paper>
	);
}
